import type { Drawing } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import { getDistanceToDrawingPath } from './BezierSampling.utils'

export interface ControlPoint {
	drawingId: string
	pointId: string
	x: number
	y: number
	color: string
}

/**
 * Filter drawings that should show control nodes based on cursor proximity and selection.
 * Returns union of hovered drawings (within proximityThreshold) and selected drawings.
 */
export function filterDrawingsNearCursor(
	drawings: Drawing[],
	selectedDrawingIds: string[],
	cursorPosition: { x: number; y: number } | null | undefined,
	coordSystem: FieldCoordinateSystem,
	proximityThreshold: number,
): Drawing[] {
	const hoveredDrawingIds = new Set<string>()

	// Find drawings within proximity threshold (hovered)
	if (cursorPosition) {
		for (const drawing of drawings) {
			const distance = getDistanceToDrawingPath(
				cursorPosition,
				drawing,
				coordSystem,
			)
			if (distance <= proximityThreshold) {
				hoveredDrawingIds.add(drawing.id)
			}
		}
	}

	// Union: show nodes for drawings that are hovered OR selected
	return drawings.filter(d =>
		hoveredDrawingIds.has(d.id) || selectedDrawingIds.includes(d.id),
	)
}

/**
 * Extract control points from drawings, excluding points linked to players.
 */
export function extractControlPoints(drawingsToShowNodes: Drawing[]): ControlPoint[] {
	return drawingsToShowNodes.flatMap(drawing =>
		Object.entries(drawing.points)
			.filter(([pointId]) => !(drawing.playerId && drawing.linkedPointId === pointId))
			.map(([, point]) => ({
				drawingId: drawing.id,
				pointId: point.id,
				x: point.x,
				y: point.y,
				color: drawing.style.color,
			})),
	)
}
